import { MoneyBackIcon } from "@xola/icons";
import { almostZero, Badge } from "@xola/ui-kit";
import React, { useMemo } from "react";
import {
    Audit,
    LineItemObject,
    LineItemStatus,
    LineItemVisibility,
    PaymentMethod,
    Purchase,
    PurchaseItem,
    RemoteCard,
} from "types";
import { useSeller } from "../../../../hooks/useAccount";
import { getPaymentMethodName } from "../../../../models/Payment";
import { Currency } from "../../../Common/Currency";
import { PaymentMethodIcon } from "../../../Common/PaymentMethodIcon";
import { TimelineItem, TimelineItemReason } from "../TimelineItem";
import { useCollection } from "@/hooks/useCollection";

interface ReturnPaymentTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
}

export const ReturnPaymentTimelineItem = ({
    audit,
    purchase,
    item,
    showDate = false,
}: ReturnPaymentTimelineItemProps) => {
    const seller = useSeller();

    const [amount, method, reason, auditCard, meta, auditLineItemId] = getAmount(audit, purchase);

    const isCreditCard = method === PaymentMethod.CREDIT_CARD;

    // Fetch saved cards only if its a cc line item and meta doesn't exist.
    const { data: savedCards } = useCollection<RemoteCard>(["purchases", purchase.id, "remoteCards"], null, {
        enabled: isCreditCard && !meta,
    });

    const purchaseLineItem = purchase.lineItems.find((lineItem) => lineItem.id === auditLineItemId);

    const card = useMemo(() => {
        if (!isCreditCard) {
            return null;
        }

        if (meta) {
            return meta;
        }

        const savedCard = savedCards?.find((savedCard) => savedCard.id === auditCard);
        const lineItemMetaCardInfo = purchaseLineItem?.payment?.meta?.cardSummary;

        return lineItemMetaCardInfo ?? savedCard;
    }, [savedCards, auditCard, isCreditCard, meta, purchaseLineItem]);

    if (!ReturnPaymentTimelineItem.supportsAudit(purchase, item, audit)) {
        return null;
    }

    if (!method) {
        return null;
    }

    const title = method === PaymentMethod.STORE_CREDIT ? "Transfer Out" : "Return Payment";

    return (
        <TimelineItem
            title={title}
            audit={audit}
            icon={<MoneyBackIcon />}
            color="danger"
            showDate={showDate}
            purchase={purchase}
        >
            <div className="flex flex-col space-y-2">
                <div className="flex flex-row space-x-2">
                    <Currency shouldRemoveTrailingZeroes={false}>{amount}</Currency>

                    <Badge color="secondary" icon={<PaymentMethodIcon method={method} />}>
                        {getPaymentMethodName(seller, method)}
                        {card && <span>&nbsp;*{card?.last4}</span>}
                    </Badge>
                </div>
                <TimelineItemReason>{reason}</TimelineItemReason>
            </div>
        </TimelineItem>
    );
};

ReturnPaymentTimelineItem.supportsAudit = (_purchase: Purchase, _item: PurchaseItem, audit: Audit) => {
    const [amount] = getAmount(audit, _purchase);
    return !almostZero(amount);
};

const getAmount = (
    audit: Audit,
    _purchase: Purchase,
): [number, PaymentMethod?, string?, string?, RemoteCard?, string?] => {
    if (!audit.data?.lineItems) {
        return [0];
    }

    const values: any = Object.values(audit.data.lineItems);
    for (const value of values) {
        if (
            _purchase?.lineItems?.some(
                (lineItem) => lineItem.id === value.new?.id && lineItem.status === LineItemStatus.FAILED,
            )
        ) {
            return [0];
        }

        // Reverse-funding voids the original payment with an internal "Remove Payment" refund line (visibility
        // "none"). That is not a customer-facing return, so skip it — only genuine returns are surfaced.
        if (value.new?.visibility === LineItemVisibility.NONE) {
            continue;
        }

        if (value.old === null && value.new.object === LineItemObject.REFUND && value.new.amount) {
            return [
                value.new.amount,
                value.new.refundToStoreCredit ? PaymentMethod.STORE_CREDIT : value.new.payment.method,
                value.new.payment.comment ?? value.new.name,
                value.new.payment?.card ?? null,
                value.new.payment?.meta?.cardSummary ?? null,
                value.new.id,
            ];
        }
    }

    return [0];
};
